import React, { useEffect, useState, useRef } from 'react';
import { format, addDays, startOfWeek, differenceInDays, parseISO, isValid } from 'date-fns';
import { Plus, X } from 'lucide-react';
import { Task, OfficeId } from '../../lib/types';
import { OFFICES, getOffice } from '../../lib/config';
import { apiListTasks, apiCreateTask } from '../../lib/api';
import OfficeContextBanner from '../OfficeContextBanner';

interface GanttChartProps {
  officeId?: OfficeId;
}

const WEEKS = 12; // Total weeks displayed
const WEEKS_PAST = 2; // Weeks before today

/**
 * GanttChart — timeline view with swimlanes per office.
 * Task bars are colored by office. Click a bar to see task details.
 */
export default function GanttChart({ officeId }: GanttChartProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Task | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const today = new Date();
  const startDate = startOfWeek(addDays(today, -(WEEKS_PAST * 7)));
  const totalDays = WEEKS * 7;

  const fetchTasks = () => {
    setLoading(true);
    apiListTasks(officeId)
      .then((all) => setTasks(all.filter((t) => t.start_date || t.end_date)))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTasks();
  }, [officeId]);

  const addTask = async (task: Partial<Task>) => {
    const created = await apiCreateTask({ ...task, column: 'todo' });
    // Only add to display if it has dates
    if (created.start_date || created.end_date) {
      setTasks((prev) => [...prev, created]);
    }
    setShowAddModal(false);
  };

  const offices = officeId ? OFFICES.filter((o) => o.id === officeId) : OFFICES;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="h-6 w-6 rounded-full border-2 border-gray-200 border-t-gray-600 animate-spin" />
      </div>
    );
  }

  const dayWidth = 28; // px per day

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="gantt-chart">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 shrink-0 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <OfficeContextBanner officeId={officeId} />
          <span className="text-xs text-gray-400">{tasks.length} dated tasks</span>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-xs text-gray-400">
            {format(startDate, 'MMM d')} — {format(addDays(startDate, totalDays), 'MMM d, yyyy')}
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-medium hover:bg-gray-700 transition-colors active:scale-95"
            data-testid="gantt-add-task-button"
          >
            <Plus size={13} />
            New Task
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {/* Date axis */}
        <div className="flex sticky top-0 z-10 bg-white border-b border-gray-100">
          {/* Label column */}
          <div className="w-36 shrink-0 border-r border-gray-100" />
          {/* Week headers */}
          {Array.from({ length: WEEKS }).map((_, wi) => {
            const weekStart = addDays(startDate, wi * 7);
            return (
              <div
                key={wi}
                className="shrink-0 border-r border-gray-100 px-2 py-2"
                style={{ width: dayWidth * 7 }}
              >
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                  {format(weekStart, 'MMM d')}
                </span>
              </div>
            );
          })}
        </div>

        {/* Today line + swimlanes */}
        <div className="relative">
          {/* Today line */}
          {(() => {
            const todayOffset = differenceInDays(today, startDate);
            if (todayOffset >= 0 && todayOffset <= totalDays) {
              return (
                <div
                  className="absolute top-0 bottom-0 w-px bg-blue-400 z-20 opacity-60"
                  style={{ left: 144 + todayOffset * dayWidth }}
                />
              );
            }
            return null;
          })()}

          {offices.map((office) => {
            const officeTasks = tasks.filter((t) => t.office_id === office.id);
            return (
              <div key={office.id} className="border-b border-gray-100">
                {/* Swimlane header */}
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50/60 border-b border-gray-100">
                  <div
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: office.color }}
                  />
                  <span className="text-xs font-semibold text-gray-700">{office.name}</span>
                  <span className="text-[10px] text-gray-400">({officeTasks.length} tasks)</span>
                </div>

                {/* Task rows */}
                {officeTasks.length === 0 ? (
                  <div
                    className="h-10 flex items-center relative"
                    style={{ width: 144 + totalDays * dayWidth }}
                  >
                    <div className="w-36 shrink-0 pl-4">
                      <span className="text-[10px] text-gray-400">No dated tasks</span>
                    </div>
                    {/* Grid lines */}
                    {Array.from({ length: WEEKS }).map((_, wi) => (
                      <div
                        key={wi}
                        className="absolute top-0 bottom-0 border-r border-gray-50"
                        style={{ left: 144 + wi * 7 * dayWidth, width: 7 * dayWidth }}
                      />
                    ))}
                  </div>
                ) : (
                  officeTasks.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      startDate={startDate}
                      totalDays={totalDays}
                      dayWidth={dayWidth}
                      weeks={WEEKS}
                      color={office.color}
                      onClick={() => setSelected(task)}
                      isSelected={selected?.id === task.id}
                    />
                  ))
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <TaskDetailDrawer task={selected} onClose={() => setSelected(null)} />
      )}

      {/* Add Task Modal */}
      {showAddModal && (
        <AddTaskModal
          officeId={officeId}
          onAdd={addTask}
          onCancel={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}

// ── Task Row ──────────────────────────────────────────────────────────────────

function TaskRow({
  task, startDate, totalDays, dayWidth, weeks, color, onClick, isSelected,
}: {
  task: Task; startDate: Date; totalDays: number; dayWidth: number; weeks: number;
  color: string; onClick: () => void; isSelected: boolean;
}) {
  const start = task.start_date ? parseISO(task.start_date) : null;
  const end = task.end_date ? parseISO(task.end_date) : null;
  if (!start || !isValid(start)) return null;

  const barEnd = end && isValid(end) ? end : addDays(start, 3);
  const offsetDays = differenceInDays(start, startDate);
  const durationDays = Math.max(1, differenceInDays(barEnd, start) + 1);

  const barLeft = offsetDays * dayWidth;
  const barWidth = durationDays * dayWidth;

  return (
    <div
      className="flex items-center h-9 relative hover:bg-gray-50 transition-colors"
      style={{ width: 144 + totalDays * dayWidth }}
    >
      {/* Task label */}
      <div className="w-36 shrink-0 pl-4 pr-2">
        <span className="text-[10px] text-gray-600 truncate block leading-tight">{task.title}</span>
        {task.agent_name && (
          <span className="text-[9px] text-gray-400">{task.agent_name}</span>
        )}
      </div>

      {/* Grid lines */}
      {Array.from({ length: weeks }).map((_, wi) => (
        <div
          key={wi}
          className="absolute top-0 bottom-0 border-r border-gray-100"
          style={{ left: 144 + wi * 7 * dayWidth, width: 7 * dayWidth }}
        />
      ))}

      {/* Task bar */}
      {barLeft + 144 < 144 + totalDays * dayWidth && barLeft + barWidth > 0 && (
        <button
          onClick={onClick}
          className="absolute h-5 rounded-md flex items-center px-2 cursor-pointer transition-opacity hover:opacity-80 active:scale-y-95"
          style={{
            left: 144 + Math.max(0, barLeft),
            width: Math.min(barWidth, totalDays * dayWidth - Math.max(0, barLeft)),
            backgroundColor: color,
            border: isSelected ? '2px solid rgba(0,0,0,0.3)' : '2px solid transparent',
          }}
          data-testid={`gantt-bar-${task.id}`}
        >
          <span className="text-[9px] font-semibold text-white truncate">{task.title}</span>
        </button>
      )}
    </div>
  );
}

// ── Task Detail Drawer ────────────────────────────────────────────────────────

function TaskDetailDrawer({ task, onClose }: { task: Task; onClose: () => void }) {
  const office = getOffice(task.office_id);
  return (
    <div
      className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-30 animate-slide-up"
      data-testid="gantt-task-detail"
    >
      <div className="flex items-start justify-between max-w-xl">
        <div>
          <div className="flex items-center gap-2 mb-1">
            {office && (
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: office.color }} />
            )}
            <span className="text-sm font-semibold text-gray-900">{task.title}</span>
          </div>
          {task.description && (
            <p className="text-xs text-gray-500 mb-2">{task.description}</p>
          )}
          <div className="flex gap-4 text-xs text-gray-500">
            {task.start_date && <span>Start: {task.start_date}</span>}
            {task.end_date && <span>End: {task.end_date}</span>}
            <span className="capitalize">{task.column}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 ml-4"
          data-testid="gantt-detail-close"
        >
          ×
        </button>
      </div>
    </div>
  );
}

// ── Add Task Modal ─────────────────────────────────────────────────────────

function AddTaskModal({
  officeId, onAdd, onCancel,
}: {
  officeId?: string;
  onAdd: (task: Partial<Task>) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [selectedOffice, setSelectedOffice] = useState(officeId || 'home');
  const [agentName, setAgentName] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" data-testid="gantt-add-task-modal">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Add Task</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" data-testid="gantt-close-add-task-modal">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
            data-testid="gantt-add-task-title-input"
            autoFocus
          />
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Description (optional)"
            rows={2}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none"
            data-testid="gantt-add-task-desc-input"
          />
          {!officeId && (
            <select
              value={selectedOffice}
              onChange={(e) => setSelectedOffice(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
              data-testid="gantt-add-task-office-select"
            >
              {OFFICES.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          <input
            type="text"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            placeholder="Assigned agent (optional)"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
            data-testid="gantt-add-task-agent-input"
          />
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onCancel}
            className="flex-1 rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="gantt-cancel-add-task"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (!title.trim()) return;
              onAdd({ title: title.trim(), description: desc.trim() || undefined, office_id: selectedOffice, agent_name: agentName.trim() || undefined });
            }}
            disabled={!title.trim()}
            className="flex-1 rounded-lg bg-gray-900 py-2 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-40 transition-colors"
            data-testid="gantt-confirm-add-task"
          >
            Add Task
          </button>
        </div>
      </div>
    </div>
  );
}
